import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { api, ApiError } from '@/lib/api'

/**
 * Sponsored Coins (ads Phase 1): coins pinned above the consumer homepage's Top
 * list with a "Sponsored" tag. Each row names a cwc-data coin; cwc-core-v1 reads
 * `promo_sponsored_coin` for `GET /api/v2/assets/sponsored` and shows a row only
 * when state="live" AND now falls within [starts_at, ends_at] — the banner's
 * rule. Shapes mirror app/promo/dto/sponsored.go and docs/api-contract.md.
 */

/**
 * How many sponsored rows the homepage shows at most: the first this many it
 * can actually show, in Order. PRD FR-13 (owner, 2026-11-03). The server's
 * service.HomepageSponsoredSlots; the screen reads `over_limit`, not this.
 */
export const HOMEPAGE_SPONSORED_SLOTS = 3

/** The `POST /sponsored-coins` body; the editor's form uses it as its schema. */
export const sponsoredSchema = z
  .object({
    coin_id: z.number({ error: 'Pick a coin.' }).int().min(1, 'Pick a coin.'),
    /**
     * Smaller first; ties fall back to the order they were added. Not coerced:
     * z.coerce turned an emptied field into 0, which saved silently. The input
     * maps "" to undefined (SORT_ORDER_INPUT), and this rejects it.
     */
    sort_order: z
      .number({ error: 'Enter an order.' })
      .int('Whole numbers only.')
      .min(0, 'Cannot be negative.')
      .max(1_000_000, 'Keep it at or under 1000000.'),
    state: z.enum(['live', 'off']),
    // RFC3339 UTC on the wire; null means unbounded on that side.
    starts_at: z.string().nullable(),
    ends_at: z.string().nullable(),
  })
  .superRefine((value, ctx) => {
    // A window that ends before it starts can never be live, so the coin would
    // silently never show. The server checks the same, after merging a PATCH.
    // As instants, not strings: the server sends "…:00Z" and the form writes
    // "…:00.000Z", and those two formats do not sort together as text.
    if (value.starts_at && value.ends_at && Date.parse(value.ends_at) <= Date.parse(value.starts_at)) {
      ctx.addIssue({ code: 'custom', path: ['ends_at'], message: 'The end must come after the start.' })
    }
  })

export type SponsoredInput = z.infer<typeof sponsoredSchema>

/** react-hook-form's register options for the Order input: "" stays empty instead of becoming 0. */
export const SORT_ORDER_INPUT = {
  setValueAs: (value: unknown) => (value === '' || value == null ? undefined : Number(value)),
}

/**
 * The PATCH body for an edit: only the fields the operator changed, by
 * react-hook-form's dirtyFields. Resending the whole form would write back the
 * values loaded when the dialog opened — switching a row live again that
 * another operator had switched off meanwhile, with the audit naming whoever
 * saved. A cleared bound is dirty with value null, which the server reads as
 * "clear it" (dto.NullableString).
 */
export function editPatch(
  values: SponsoredInput,
  dirty: Partial<Record<keyof SponsoredInput, unknown>>,
): Partial<SponsoredInput> {
  const out: Partial<Record<keyof SponsoredInput, unknown>> = {}
  for (const key of Object.keys(values) as (keyof SponsoredInput)[]) {
    if (dirty[key]) out[key] = values[key]
  }
  return out as Partial<SponsoredInput>
}

export interface SponsoredCoinRef {
  id: number
  name: string
  symbol: string
  slug: string
  logo: string
  /**
   * Whether cwc-data has market data for it. cwc-core-v1 builds the homepage row
   * from that data, so a coin without it is configured here but never shown.
   */
  has_market: boolean
}

export interface SponsoredCoin {
  id: number
  /** null: the coin was removed by cwc-data (or is not a crypto coin) — never shown on the site. */
  coin: SponsoredCoinRef | null
  sort_order: number
  state: 'live' | 'off'
  starts_at: string | null
  ends_at: string | null
  /** state=live and the server's clock inside the window. Says nothing about the coin or the slot limit. */
  live: boolean
  /**
   * Live, coin present, with market data — but an earlier such row in Order has
   * the same coin, and a coin shows once. Takes no slot.
   */
  duplicate_coin: boolean
  /**
   * Live, coin present, with market data, not a duplicate — but past the
   * homepage's HOMEPAGE_SPONSORED_SLOTS: the rows before it in Order took them.
   */
  over_limit: boolean
  updated_by: string | null
  updated_at: string | null
  created_at: string | null
}

export interface CoinSearchResult extends SponsoredCoinRef {
  /** Decimal string, null when cwc-data has none. */
  market_cap: string | null
}

const KEY = ['sponsored-coins']

/**
 * A 2001 means the row was deleted by someone else while this list was open.
 * The list is stale, so reload it and the removed row disappears — what
 * ConfirmAction's onConflict does for a delete, here for the toggle and the
 * editor, which do not go through ConfirmAction. The error still reaches the
 * caller, which shows its message.
 */
function refetchOnConflict(queryClient: ReturnType<typeof useQueryClient>) {
  return (error: unknown) => {
    if (error instanceof ApiError && error.isInvalidState) {
      void queryClient.invalidateQueries({ queryKey: KEY })
    }
  }
}

export function useSponsoredCoins() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => api.get<SponsoredCoin[]>('/admin/sponsored-coins'),
  })
}

export function useSaveSponsoredCoin() {
  const queryClient = useQueryClient()
  return useMutation({
    // A create sends the whole form; an edit sends editPatch's changed fields.
    mutationFn: ({ id, input }: { id?: number; input: SponsoredInput | Partial<SponsoredInput> }) =>
      id === undefined
        ? api.post<SponsoredCoin>('/admin/sponsored-coins', input)
        : api.patch<SponsoredCoin>(`/admin/sponsored-coins/${id}`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
    onError: refetchOnConflict(queryClient),
  })
}

/**
 * The inline on/off switch. Sends only `state`, so it cannot write back a stale
 * copy of a window someone else changed while this list was open.
 */
export function useSetSponsoredState() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, state }: { id: number; state: SponsoredInput['state'] }) =>
      api.patch<SponsoredCoin>(`/admin/sponsored-coins/${id}`, { state }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
    onError: refetchOnConflict(queryClient),
  })
}

export function useDeleteSponsoredCoin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<null>(`/admin/sponsored-coins/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  })
}

/** The picker's search. Disabled for a blank query, which the server answers with []. */
export function useCoinSearch(q: string) {
  const query = q.trim()
  return useQuery({
    queryKey: ['coin-search', query],
    queryFn: () =>
      api.get<CoinSearchResult[]>(`/admin/coins/search?${new URLSearchParams({ q: query, limit: '20' })}`),
    enabled: query.length > 0,
    staleTime: 60_000,
  })
}
